<script setup>
import { ref, reactive, watch, onMounted } from 'vue';

const props = defineProps({
    fillColors: {
        type: Array,
        default: () => ['#7814d7'],
    },
    labels: {
        type: Array,
        required: false,
        default: () => ['Label 1', 'Label 2', 'Label 3', 'Label 4'],
    },
    lazyRender: {
        type: Boolean,
        default: false,
    },
    series: {
        type: Array,
        required: false,
        default: () => [
            {
                name: 'Series 1',
                data: [50, 40, 35, 20],
            },
        ],
    },
    showDataLabels: {
        type: Boolean,
        default: false,
    },
    dataLabelPrefix: {
        type: String,
        default: '',
    },
    dataLabelSuffix: {
        type: String,
        default: '',
    },
    showLegend: {
        type: Boolean,
        default: false,
    },
    showTooltip: {
        type: Boolean,
        default: false,
    },
    strokeCurve: {
        type: String,
        default: 'smooth',
    },
});

const formatValue = (val) => {
    if (val === null || val === undefined) {
        return '';
    }

    const num = Number(val);

    if (Number.isNaN(num)) {
        return `${props.dataLabelPrefix}${val}${props.dataLabelSuffix}`;
    }

    return `${props.dataLabelPrefix}${num.toLocaleString(undefined, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
    })}${props.dataLabelSuffix}`;
};

const cseries = ref([]);

const chartOptions = reactive({
    chart: {
        animations: {
            enabled: true,
            easing: 'easeinout',
            speed: 600,
            animateGradually: {
                enabled: true,
                delay: 120,
            },
            dynamicAnimation: {
                enabled: true,
                speed: 450,
            },
        },
        toolbar: {
            show: false,
        },
    },
    colors: props.fillColors,
    stroke: {
        curve: props.strokeCurve,
        width: 3,
    },
    markers: {
        size: 4,
        colors: props.fillColors,
        strokeColors: '#ffffff',
        strokeWidth: 2,
        hover: {
            sizeOffset: 2,
        },
    },
    legend: {
        show: props.showLegend,
        labels: {
            colors: props.fillColors,
        },
        markers: {
            width: 10,
            height: 10,
            fillColors: props.fillColors,
        },
    },
    tooltip: {
        enabled: props.showTooltip,
        y: {
            formatter(val) {
                return formatValue(val);
            },
        },
    },
    xaxis: {
        categories: props.labels,
    },
    dataLabels: {
        enabled: props.showDataLabels,
        formatter(val) {
            return formatValue(val);
        },
    },
    yaxis: {
        labels: {
            formatter(val) {
                return formatValue(val);
            },
        },
    },
});

const target = ref(null);
const isVisible = ref(false);

const observer = new IntersectionObserver(
    ([entry]) => {
        isVisible.value = entry.isIntersecting;
    },
    {
        threshold: 0.5,
    }
);

watch(
    () => isVisible.value,
    (newVal) => {
        if (props.lazyRender && newVal) {
            cseries.value = props.series;
        }
    }
);

onMounted(() => {
    if (props.lazyRender) {
        cseries.value = props.series.map((dataset) => ({
            ...dataset,
            data: dataset.data.map(() => 0),
        }));
    } else {
        cseries.value = props.series;
    }

    if (target.value?.$el) {
        observer.observe(target.value.$el);
    }
});
</script>

<template>
    <apexchart
        ref="target"
        type="line"
        height="350"
        :options="chartOptions"
        :series="cseries"
    ></apexchart>
</template>
